<template>
    <div class="ticket-list-container">
        <div class="ticket-list-header">
            <div class="d-flex justify-space-between align-center">
                <div class="d-flex align-center">
                    <v-btn
                        v-if="selectedCompanyId"
                        icon
                        size="small"
                        variant="text"
                        color="white"
                        class="mr-2"
                        :title="$t('messages.refresh_tickets')"
                        @click="refreshTickets"
                    >
                        <v-icon>mdi-refresh</v-icon>
                    </v-btn>
                    <v-icon v-else color="white" class="mr-2">mdi-ticket-outline</v-icon>
                    <span class="text-h6">{{ $t('messages.tickets') }}</span>
                </div>
                <v-tooltip v-if="selectedCompanyId && showCreateButton" :text="!canCreateTicket ? $t('messages.no_respondents_tooltip') : ''" :disabled="canCreateTicket" location="bottom">
                    <template #activator="{ props }">
                        <span v-bind="props">
                            <v-btn color="teal" size="small" :disabled="!canCreateTicket" @click="createNewTicket">
                                <v-icon start>mdi-plus</v-icon>
                                {{ $t('messages.new_ticket') }}
                            </v-btn>
                        </span>
                    </template>
                </v-tooltip>
            </div>

            <!-- Search input -->
            <v-text-field
                v-model="searchQuery"
                :placeholder="$t('messages.search_tickets')"
                variant="outlined"
                density="compact"
                prepend-inner-icon="mdi-magnify"
                hide-details
                class="mt-2 messages-search-input"
                bg-color="white"
                persistent-placeholder
            />
        </div>

        <div class="ticket-list-content">
            <!-- Loading state -->
            <div v-if="isLoading" class="d-flex flex-column align-center justify-center py-5">
                <v-progress-circular indeterminate color="var(--zsh-primary)" class="mb-3" />
                <span>{{ loadingProgress || $t('messages.loading_tickets') }}</span>
            </div>

            <!-- Error state -->
            <v-alert v-else-if="error" type="error" class="mt-3">
                {{ error }}
            </v-alert>

            <!-- No company selected state -->
            <div v-else-if="!selectedCompanyId" class="text-center py-5">
                <v-icon color="var(--zsh-primary)" size="48">mdi-domain-remove</v-icon>
                <div class="mt-2">{{ $t('messages.select_company_to_view_tickets') }}</div>
            </div>

            <!-- Empty state -->
            <div v-else-if="filteredTickets.length === 0" class="text-center py-5">
                <v-icon color="var(--zsh-primary)" size="48">mdi-ticket-outline</v-icon>
                <div class="mt-2">{{ $t('messages.no_tickets_found') }}</div>
                <v-tooltip v-if="showCreateButton" :text="!canCreateTicket ? $t('messages.no_respondents_tooltip') : ''" :disabled="canCreateTicket" location="bottom">
                    <template #activator="{ props }">
                        <span v-bind="props">
                            <v-btn color="teal" class="mt-3" :disabled="!canCreateTicket" @click="createNewTicket">
                                {{ $t('messages.create_new_ticket_button') }}
                            </v-btn>
                        </span>
                    </template>
                </v-tooltip>
            </div>

            <!-- Tickets list -->
            <v-list v-else density="compact" bg-color="transparent" class="tickets-scroll-list">
                <v-list-item
                    v-for="ticket in filteredTickets"
                    :key="ticket.id"
                    :active="selectedTicketId === ticket.id"
                    :class="{
                        'selected-item': selectedTicketId === ticket.id,
                        'highlighted-ticket': shouldHighlightTicket && shouldHighlightTicket(ticket),
                    }"
                    class="mb-3 rounded pa-2"
                    @click="selectTicket(ticket)"
                >
                    <template v-slot:prepend>
                        <v-avatar size="32" :color="getStatusColor(ticket.status)" class="mr-2">
                            <v-icon color="white">{{ getStatusIcon(ticket.status) }}</v-icon>
                        </v-avatar>
                    </template>

                    <v-list-item-title>{{ ticket.subject }}</v-list-item-title>
                    <v-list-item-subtitle>
                        <span class="text-caption"> #{{ ticket.id }} · {{ formatDate(ticket.createdAt) }} </span>
                    </v-list-item-subtitle>
                </v-list-item>
            </v-list>
        </div>
    </div>
</template>

<script lang="ts">
    import {Component, Prop, Vue, Emit} from 'vue-facing-decorator'
    import {format} from 'date-fns'

    interface LastMessage {
        senderId: string
        senderName: string
        isTO1Role: boolean
        timestamp: number
    }

    interface Ticket {
        id: string
        subject: string
        status: string
        createdAt: number
        updatedAt: number
        companyId: string
        lastMessage?: LastMessage
    }

    @Component
    export default class TicketList extends Vue {
        @Prop({default: () => []}) tickets!: Ticket[]
        @Prop({default: false}) isLoading!: boolean
        @Prop({default: null}) error!: string | null
        @Prop({default: null}) selectedCompanyId!: string | null
        @Prop({default: null}) selectedTicketId!: string | null
        @Prop({default: true}) showCreateButton!: boolean
        @Prop({default: true}) canCreateTicket!: boolean
        @Prop({default: null}) shouldHighlightTicket!: ((ticket: Ticket) => boolean) | null
        @Prop({default: ''}) loadingProgress!: string

        searchQuery: string = ''

        get filteredTickets(): Ticket[] {
            if (!this.searchQuery) {
                return this.tickets
            }

            const query = this.searchQuery.toLowerCase()
            return this.tickets.filter(
                ticket => ticket.subject.toLowerCase().includes(query) || ticket.id.toLowerCase().includes(query)
            )
        }

        @Emit('ticket-selected')
        selectTicket(ticket: Ticket): Ticket {
            return ticket
        }

        @Emit('create-ticket')
        createNewTicket(): void {}

        @Emit('refresh-tickets')
        refreshTickets(): void {
            // Event emitted to parent to refresh tickets list
        }

        formatDate(timestamp: number): string {
            return format(new Date(timestamp), 'MMM d, yyyy')
        }

        getStatusColor(status: string): string {
            switch (status.toLowerCase()) {
                case 'open':
                    return 'green'
                case 'pending':
                    return 'orange'
                case 'closed':
                    return 'grey'
                default:
                    return 'blue'
            }
        }

        getStatusIcon(status: string): string {
            switch (status.toLowerCase()) {
                case 'open':
                    return 'mdi-ticket'
                case 'pending':
                    return 'mdi-ticket-account'
                case 'closed':
                    return 'mdi-ticket-confirmation'
                default:
                    return 'mdi-ticket-outline'
            }
        }
    }
</script>

<style scoped>
    .ticket-list-container {
        display: flex;
        flex-direction: column;
        height: 100%;
        background-color: var(--zsh-messages-panel-bg);
        border-radius: 4px;
        color: var(--zsh-messages-text);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        border: 1px solid var(--zsh-messages-border);
        flex: 1;
    }

    .ticket-list-header {
        flex: 0 0 auto;
        height: 120px;
        padding: 16px 16px 8px 16px;
        background-color: var(--zsh-primary);
        color: white;
        border-radius: 4px 4px 0 0;
    }

    .ticket-list-content {
        flex: 1 1 auto;
        overflow: hidden;
        padding: 0 16px 16px 16px;
        display: flex;
        flex-direction: column;
        margin-top: 8px; /* Add space after header */
        min-height: 0; /* Crucial for flex items to respect parent container boundaries */
    }

    .tickets-scroll-list {
        flex: 1;
        overflow-y: auto;
        overflow-x: hidden;
        max-height: 100%; /* Use relative height instead of viewport-based calculation */
    }

    .v-list-item {
        transition: background-color 0.2s ease;
        border-radius: 4px;
        margin-bottom: 4px;
    }

    .v-list-item:hover {
        background-color: rgba(0, 0, 0, 0.03);
    }

    .selected-item {
        background-color: var(--zsh-primary) !important;
        color: white !important;
        font-weight: bold;
    }

    .selected-item .v-list-item-subtitle {
        color: rgba(255, 255, 255, 0.8) !important;
    }

    /* Highlighted ticket styling - lighter shade of teal */
    .highlighted-ticket {
        background-color: rgba(0, 150, 136, 0.15) !important; /* Light teal background */
        border-left: 3px solid var(--zsh-primary) !important;
    }

    .highlighted-ticket:hover {
        background-color: rgba(0, 150, 136, 0.25) !important; /* Slightly darker on hover */
    }

    /* Ensure selected item styling takes precedence over highlighted */
    .selected-item.highlighted-ticket {
        background-color: var(--zsh-primary) !important;
        border-left: 3px solid var(--zsh-primary) !important;
    }

    /* Disable label animations and transitions for search inputs */
    .messages-search-input {
        .v-label {
            display: none !important;
        }

        .v-field__label {
            display: none !important;
        }

        .v-field--focused .v-label,
        .v-field--focused .v-field__label {
            display: none !important;
            transform: none !important;
            transition: none !important;
            animation: none !important;
        }

        .v-field__input {
            transition: none !important;
            animation: none !important;
        }

        .v-field {
            transition: none !important;
            animation: none !important;
        }

        /* Ensure placeholder stays in place */
        input::placeholder {
            transition: none !important;
            animation: none !important;
            transform: none !important;
            opacity: 0.6 !important;
        }

        /* Remove any floating label behavior */
        .v-field--variant-outlined.v-field--focused .v-field__outline {
            transition: none !important;
        }
    }
</style>
